import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { FormInput } from '../../../components/forms'
import { personnelService, userService, type EmployeeProfile } from '../../../services'
import { useLocale } from '../../../hooks/use-locale'
import { Save, CheckCircle, CircleAlert } from 'lucide-react'

export default function EmployeeContactSection() {
  const { t } = useTranslation()
  const { formatDateTime } = useLocale()
  const { id } = useParams()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['employee-profile', id],
    queryFn: () => personnelService.getEmployeeProfile(id!),
    enabled: !!id,
  })

  const { data: user } = useQuery({
    queryKey: ['user', id],
    queryFn: () => userService.getUserById(id!),
    enabled: !!id,
  })

  const [form, setForm] = useState<Partial<EmployeeProfile>>({})
  const [savedAt, setSavedAt] = useState<Date | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    if (data) {
      setForm({
        ...data,
        workEmail: data.workEmail || user?.email || '',
        phoneMobile: data.phoneMobile || user?.phoneNumber || '',
      })
    } else if (user) {
      setForm({
        workEmail: user.email || '',
        phoneMobile: user.phoneNumber || '',
      })
    }
  }, [data, user])

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
  }

  const saveMutation = useMutation({
    mutationFn: (payload: Partial<EmployeeProfile>) =>
      personnelService.upsertEmployeeProfile(id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-profile', id] })
      queryClient.invalidateQueries({ queryKey: ['user', id] })
      setSavedAt(new Date())
      setErrorMessage(null)
    },
    onError: (error: any) => {
      setSavedAt(null)
      setErrorMessage(error?.message || t('errors.saveFailed'))
    },
  })

  const onSave = () => {
    if (!id) return
    saveMutation.mutate({
      workEmail: form.workEmail,
      privateEmail: form.privateEmail,
      phoneWork: form.phoneWork,
      phoneMobile: form.phoneMobile,
      phonePrivate: form.phonePrivate,
    })
  }

  return (
    <Card>
      <CardHeader className='flex-row items-center justify-between'>
        <div>
          <CardTitle>{t('common.contact')}</CardTitle>
          <p className='text-sm mt-1'>
            {savedAt && (
              <span className='inline-flex items-center gap-1 text-success-600'>
                <CheckCircle className='w-3.5 h-3.5' />
                {t('common.savedAt', { date: formatDateTime(savedAt) })}
              </span>
            )}
            {errorMessage && (
              <span className='inline-flex items-center gap-1 text-error-600'>
                <CircleAlert className='w-3.5 h-3.5' />
                {errorMessage}
              </span>
            )}
          </p>
        </div>
        <Button onClick={onSave} variant='primary' loading={saveMutation.isPending} disabled={isLoading}>
          <Save className='w-4 h-4 mr-1.5' />
          {t('common.save')}
        </Button>
      </CardHeader>
      <CardContent>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-5'>
          <FormInput
            label={t('common.workEmail')}
            name='workEmail'
            type='email'
            value={form.workEmail || ''}
            onChange={onChange}
            placeholder={t('users.workEmailPlaceholder')}
          />
          <FormInput
            label={t('common.privateEmail')}
            name='privateEmail'
            type='email'
            value={form.privateEmail || ''}
            onChange={onChange}
            placeholder={t('users.privateEmailPlaceholder')}
          />
          <FormInput
            label={t('common.phoneWork')}
            name='phoneWork'
            value={form.phoneWork || ''}
            onChange={onChange}
            placeholder={t('users.phonePlaceholder')}
          />
          <FormInput
            label={t('common.phoneMobile')}
            name='phoneMobile'
            value={form.phoneMobile || ''}
            onChange={onChange}
            placeholder={t('users.phonePlaceholder')}
          />
          <FormInput
            label={t('common.phonePrivate')}
            name='phonePrivate'
            value={form.phonePrivate || ''}
            onChange={onChange}
            placeholder={t('users.phonePlaceholder')}
          />
        </div>
      </CardContent>
    </Card>
  )
}
